import React, { ReactNode, useState, useEffect } from "react";
import Header from "@/components/header";
import { useApp } from "@/lib/context";
import { PullToRefreshWrapper } from "@/components/pull-to-refresh-wrapper";
import { useDesktopShell } from "@/hooks/use-desktop-shell";
import { DesktopShell } from "@/components/layout/desktop-shell";

interface AppLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  refreshQueries?: string[];
  onRefresh?: () => Promise<void> | void;
}

export function AppLayout({
  children,
  showHeader = true,
  refreshQueries = [],
  onRefresh
}: AppLayoutProps) {
  const { isAnyProfileSheetOpen } = useApp();
  const [hideHeaderSecondRow, setHideHeaderSecondRow] = useState(false);
  const desktopShellEnabled = useDesktopShell();

  useEffect(() => {
    const handleChatOpened = () => setHideHeaderSecondRow(true);
    const handleChatClosed = () => setHideHeaderSecondRow(false);

    window.addEventListener('chatConversationOpened', handleChatOpened);
    window.addEventListener('chatConversationClosed', handleChatClosed);

    return () => {
      window.removeEventListener('chatConversationOpened', handleChatOpened);
      window.removeEventListener('chatConversationClosed', handleChatClosed);
    };
  }, []);

  // Nascondi seconda riga quando profilo è aperto o chat è aperta
  const shouldHideSecondRow = isAnyProfileSheetOpen || hideHeaderSecondRow;

  const content = refreshQueries.length > 0 ? (
    <PullToRefreshWrapper
      refreshQueries={refreshQueries}
      onRefresh={onRefresh}
      className="h-full"
    >
      {children}
    </PullToRefreshWrapper>
  ) : (
    children
  );

  if (desktopShellEnabled) {
    return (
      <DesktopShell>
        <div className="mx-auto w-full max-w-6xl px-6 py-6">{content}</div>
      </DesktopShell>
    );
  }

  return (
    <>
      {/* Header globale - renderizzato una sola volta */}
      {showHeader && <Header hideSecondRow={shouldHideSecondRow} />}

      {/* Contenuto della pagina */}
      <div
        className="pb-16 lg:pb-0 lg:ml-60"
        style={{
          // Offset header + safe-area-inset-top (PWA standalone). env() = 0 in
          // Telegram/browser, quindi invariato lì.
          paddingTop: showHeader
            ? `calc(${shouldHideSecondRow ? 'var(--kg-header-row1)' : 'calc(var(--kg-header-row1) + 56px)'} + var(--safe-top))`
            : 'var(--safe-top)',
        }}
      >
        <div className="lg:max-w-6xl lg:mx-auto lg:px-6">
          {content}
        </div>
      </div>
    </>
  );
}

export default AppLayout;
